import { Controller } from "@hotwired/stimulus"

/**
 * Stimulus controller for Acutext question type interactions.
 * Handles click events on text hotspot areas and syncs with hidden checkboxes.
 *
 * Acutext questions display text with clickable "hotspot" areas (.area elements).
 * Each area has an area_index attribute that maps to a hidden checkbox
 * (id="answer_response{index}") for form submission.
 *
 * @class AcutextController
 * @extends Controller
 */
export default class extends Controller {
  /**
   * Called when controller connects to DOM.
   * Sets up click handlers and initializes visual state.
   */
  connect() {
    this.setupClickHandlers()
    this.setupExclusiveHandlers()
    this.refreshHighlights()
  }

  /**
   * Called when controller disconnects.
   * Cleans up event listeners.
   */
  disconnect() {
    if (this.clickHandler) {
      this.element.removeEventListener("mousedown", this.clickHandler)
    }
    if (this.exclusiveHandlers) {
      this.exclusiveHandlers.forEach(({ checkbox, handler }) => {
        checkbox.removeEventListener("change", handler)
      })
    }
  }

  /**
   * Sets up click handlers for .area elements using event delegation.
   * Uses mousedown to match legacy behavior.
   */
  setupClickHandlers() {
    this.clickHandler = (event) => {
      const area = event.target.closest(".area")
      if (!area) return

      const areaIndex = area.getAttribute("area_index")
      if (!areaIndex) return

      // Uncheck all exclusive options first (NOTA-style behavior)
      document.querySelectorAll(".ACU_EXCLUSIVE").forEach(checkbox => {
        checkbox.checked = false
      })

      // Toggle the corresponding checkbox
      const checkbox = document.getElementById(`answer_response${areaIndex}`)
      if (checkbox) {
        checkbox.checked = !checkbox.checked
        // Dispatch change event to trigger any listeners (like multi-select validation)
        checkbox.dispatchEvent(new Event("change", { bubbles: true }))
      }

      this.refreshHighlights()
    }

    this.element.addEventListener("mousedown", this.clickHandler)
  }

  /**
   * Sets up change handlers for exclusive (NOTA) checkboxes.
   * When an exclusive option is selected, all non-exclusive options should be deselected.
   */
  setupExclusiveHandlers() {
    this.exclusiveHandlers = []

    // Find all exclusive checkboxes (outside this controller's scope, typically in the form)
    document.querySelectorAll(".ACU_EXCLUSIVE").forEach(checkbox => {
      const handler = (event) => {
        if (checkbox.checked) {
          // When exclusive option is checked, uncheck all non-exclusive options
          document.querySelectorAll(".ACU_NON_EXCLUSIVE").forEach(nonExclusive => {
            nonExclusive.checked = false
          })
          // Refresh visual state of hotspot areas
          this.refreshHighlights()
        }
      }

      checkbox.addEventListener("change", handler)
      this.exclusiveHandlers.push({ checkbox, handler })
    })
  }

  /**
   * Updates visual highlighting to match checkbox state.
   * Adds area_HL class to areas whose checkboxes are checked.
   */
  refreshHighlights() {
    this.element.querySelectorAll(".area").forEach(area => {
      const areaIndex = area.getAttribute("area_index")
      if (!areaIndex) return

      const checkbox = document.getElementById(`answer_response${areaIndex}`)
      if (!checkbox) return

      area.classList.toggle("area_HL", checkbox.checked)
    })
  }
}
